import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Anchor, Loader2, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasValidSession, setHasValidSession] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle the recovery token and session establishment
  useEffect(() => {
    let isMounted = true;

    const initializeRecovery = async () => {
      try {
        // First, check if there's already a session (user might have refreshed)
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          if (isMounted) {
            setHasValidSession(true);
            setInitializing(false);
          }
          return;
        }

        // If no session, wait for the PASSWORD_RECOVERY event
        // The URL hash contains the recovery token that Supabase will process
      } catch (err) {
        console.error("Error checking session:", err);
      }
    };

    // Listen for auth state changes - specifically PASSWORD_RECOVERY
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        if (event === "PASSWORD_RECOVERY" && session) {
          setHasValidSession(true);
          setError(null);
          setInitializing(false);
        } else if (event === "SIGNED_IN" && session) {
          // Also handle SIGNED_IN in case the recovery already completed
          setHasValidSession(true);
          setInitializing(false);
        }
      }
    );

    initializeRecovery();

    // Set a timeout to stop showing loading if no recovery event fires
    const timeout = setTimeout(() => {
      if (isMounted && initializing) {
        setInitializing(false);
        // Check one more time if we have a session
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) {
            setHasValidSession(true);
          }
        });
      }
    }, 3000);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    // Double-check we have a valid session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setError("Your reset link has expired. Please request a new password reset.");
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      setSuccess(true);
      toast({
        title: "Password updated",
        description: "Your password has been successfully reset.",
      });

      // Redirect to app after a short delay
      setTimeout(() => {
        navigate("/app/launch-kit", { replace: true });
      }, 2000);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while initializing
  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white">
              <Anchor className="h-8 w-8 text-accent" />
              <span className="text-2xl font-bold">HostCaptain</span>
            </Link>
          </div>
          <Card className="bg-card">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Verifying your reset link...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white">
            <Anchor className="h-8 w-8 text-accent" />
            <span className="text-2xl font-bold">HostCaptain</span>
          </Link>
        </div>

        <Card className="bg-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-foreground">
              {success ? "Password Reset!" : hasValidSession ? "Set new password" : "Invalid or Expired Link"}
            </CardTitle>
            <CardDescription>
              {success 
                ? "Your password has been updated successfully."
                : hasValidSession 
                  ? "Enter your new password below."
                  : "This password reset link is invalid or has expired."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <Alert className="border-primary/20 bg-primary/5">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-foreground">
                    You'll be redirected to the app shortly...
                  </AlertDescription>
                </Alert>
              </div>
            ) : hasValidSession ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-primary hover:opacity-90"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Reset Password
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please request a new password reset link.
                  </AlertDescription>
                </Alert>
                <Button 
                  asChild
                  className="w-full bg-gradient-primary hover:opacity-90"
                >
                  <Link to="/forgot-password">Request New Link</Link>
                </Button>
              </div>
            )}

            {!success && hasValidSession && (
              <div className="mt-6 text-center">
                <Link
                  to="/auth"
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Back to sign in
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
